import { useCallback, useEffect, useRef, useState } from "react";

import type { FaceDetectionResult } from "../lib/faceDetection";
import { isProgressMessage, type WorkerMessage } from "../types/worker";

export const workerAllowed = typeof OffscreenCanvas !== "undefined";

export function useFaceDetectionWorker() {
  const workerRef = useRef<Worker | null>(null);
  const callbacksRef = useRef<Map<number, (result: unknown) => void>>(
    new Map(),
  );
  const lastIdRef = useRef(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!workerAllowed) return;

    const worker = new Worker(
      new URL("../workers/face-detection.worker.ts", import.meta.url),
      { type: "module" },
    );
    workerRef.current = worker;

    worker.addEventListener("message", (e: MessageEvent<WorkerMessage>) => {
      if (isProgressMessage(e.data)) {
        setProgress(e.data.progress);
        return;
      }
      const { id, result } = e.data as { id: number; result: unknown };
      const resolve = callbacksRef.current.get(id);
      if (resolve) {
        callbacksRef.current.delete(id);
        resolve(result);
      }
    });

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  const loadModels = useCallback(async (): Promise<boolean> => {
    if (!workerAllowed) {
      const { loadModelsFallback } = await import(
        "../lib/faceDetectionFallback"
      );
      await loadModelsFallback((p) => setProgress(p));
      return true;
    }
    return new Promise((resolve) => {
      const id = lastIdRef.current++;
      callbacksRef.current.set(id, (result) => resolve(result as boolean));
      workerRef.current?.postMessage({ id, type: "loadModels", data: null });
    });
  }, []);

  const detect = useCallback(
    async (
      source: ImageBitmap | HTMLVideoElement,
      mobile: boolean,
    ): Promise<FaceDetectionResult | null> => {
      if (!workerAllowed && source instanceof HTMLVideoElement) {
        const { detectFallback } = await import("../lib/faceDetectionFallback");
        return await detectFallback(source, mobile);
      }
      if (workerAllowed && source instanceof ImageBitmap) {
        return new Promise((resolve) => {
          const id = lastIdRef.current++;
          callbacksRef.current.set(id, (result) =>
            resolve(result as FaceDetectionResult | null),
          );
          workerRef.current?.postMessage({
            id,
            type: "detect",
            data: { bitmap: source, mobile },
          });
        });
      }
      return Promise.resolve(null);
    },
    [],
  );

  return { loadModels, detect, progress };
}
